import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  ArrowLeftEndOnRectangleIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  BoltIcon,
  PowerIcon,
  BanknotesIcon,
  XMarkIcon,
  MapIcon,
  CameraIcon,
  BellIcon,
  BellSlashIcon,
} from '@heroicons/react/24/outline';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import supabase from './utils/supabaseClient';
import { SERVICES, formatBRL } from './utils/pricing';

const STATUS_NEXT = {
  aceito:     { next: 'em_coleta', label: 'Cheguei na coleta', tsField: 'aceito_at',   pastLabel: 'Aceito' },
  em_coleta:  { next: 'em_rota',   label: 'Coletado, indo entregar', tsField: 'coletado_at', pastLabel: 'Na coleta' },
  em_rota:    { next: 'entregue',  label: 'Entreguei',         tsField: 'entregue_at', pastLabel: 'A caminho' },
};

const fmtTime = (iso) => iso
  ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  : '—';

const EntregadorApp = () => {
  const [authState, setAuthState] = useState('loading'); // loading | unauth | not_approved | ready
  const [, setSession]            = useState(null);
  const [entregador, setEntregador] = useState(null);
  const [available, setAvailable]   = useState([]);
  const [active, setActive]         = useState(null); // pedido em andamento
  const [history, setHistory]       = useState([]);   // pedidos entregues hoje
  const [online, setOnline]         = useState(false);
  const [loading, setLoading]       = useState(false);
  const [acceptingId, setAcceptingId] = useState(null);
  const [wallet, setWallet]           = useState({ aReceber: 0, qtdPendente: 0, totalGanho: 0 });
  const [repasses, setRepasses]       = useState([]);
  const [walletOpen, setWalletOpen]   = useState(false);
  const [deleteOpen, setDeleteOpen]   = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [proofOpen, setProofOpen]     = useState(false);
  const [proofFile, setProofFile]     = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [submittingProof, setSubmittingProof] = useState(false);
  const [notifPermission, setNotifPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported'
  );
  const knownAvailableIds = useRef(new Set());
  const isFirstLoad = useRef(true);

  // Auth + dados do entregador
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      setSession(s);
      if (!s) { setAuthState('unauth'); return; }
      await loadEntregador();
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_e, s) => {
      setSession(s);
      if (s) await loadEntregador();
      else { setAuthState('unauth'); setEntregador(null); }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEntregador = useCallback(async () => {
    const { data, error } = await supabase
      .from('entregadores')
      .select('id, primeiro_nome, sobrenome, email, status, tipo_veiculo')
      .limit(1)
      .single();
    if (error || !data) { setAuthState('not_approved'); return; }
    setEntregador(data);
    setAuthState(data.status === 'aprovado' ? 'ready' : 'not_approved');
  }, []);

  // Carregar pedidos
  const loadPedidos = useCallback(async () => {
    if (!entregador) return;
    setLoading(true);

    // Pedido em andamento (status entre aceito..em_rota)
    const { data: act } = await supabase
      .from('pedidos')
      .select('*')
      .eq('entregador_id', entregador.id)
      .in('status', ['aceito', 'em_coleta', 'em_rota'])
      .order('aceito_at', { ascending: false })
      .limit(1);
    setActive(act?.[0] ?? null);

    // Disponíveis (só carrega se está online e sem ativo)
    if (online && !act?.[0]) {
      const { data: avail } = await supabase
        .from('pedidos')
        .select('*')
        .eq('status', 'buscando_entregador')
        .is('entregador_id', null)
        .order('created_at', { ascending: false })
        .limit(20);
      setAvailable(avail ?? []);
    } else {
      setAvailable([]);
    }

    // Histórico do dia
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const { data: hist } = await supabase
      .from('pedidos')
      .select('codigo_rastreio, preco, entregue_at, endereco_entrega, servico_id')
      .eq('entregador_id', entregador.id)
      .eq('status', 'entregue')
      .gte('entregue_at', startOfDay.toISOString())
      .order('entregue_at', { ascending: false });
    setHistory(hist ?? []);

    setLoading(false);
  }, [entregador, online]);

  useEffect(() => { loadPedidos(); }, [loadPedidos]);

  // Carteira
  const loadWallet = useCallback(async () => {
    if (!entregador) return;
    const [{ data: pend }, { data: rep }] = await Promise.all([
      supabase
        .from('pedidos')
        .select('valor_entregador, repasse_id, status')
        .eq('entregador_id', entregador.id)
        .eq('status', 'entregue'),
      supabase
        .from('repasses')
        .select('id, valor_total, quantidade_pedidos, metodo, referencia, pago_em')
        .eq('entregador_id', entregador.id)
        .order('pago_em', { ascending: false })
        .limit(20),
    ]);
    const aReceberItens = (pend ?? []).filter((p) => !p.repasse_id);
    setWallet({
      aReceber:    aReceberItens.reduce((s, p) => s + Number(p.valor_entregador || 0), 0),
      qtdPendente: aReceberItens.length,
      totalGanho:  (pend ?? []).reduce((s, p) => s + Number(p.valor_entregador || 0), 0),
    });
    setRepasses(rep ?? []);
  }, [entregador]);

  useEffect(() => { loadWallet(); }, [loadWallet]);

  // Polling enquanto online
  useEffect(() => {
    if (authState !== 'ready') return;
    const t = setInterval(loadPedidos, 15000);
    return () => clearInterval(t);
  }, [authState, loadPedidos]);

  // Geolocalização — envia posição para Supabase a cada update do GPS
  useEffect(() => {
    if (!online || !entregador || !navigator.geolocation) return;

    let lastSent = 0;
    const watchId = navigator.geolocation.watchPosition(
      async (pos) => {
        const now = Date.now();
        if (now - lastSent < 12000) return; // throttle ~12s
        lastSent = now;

        const payload = {
          entregador_id: entregador.id,
          lat:  pos.coords.latitude,
          lng:  pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          updated_at: new Date().toISOString(),
        };
        const { error } = await supabase
          .from('entregador_localizacao')
          .upsert(payload, { onConflict: 'entregador_id' });
        if (error) console.error('upsert localização:', error);
      },
      (err) => {
        console.warn('GPS:', err.message);
        toast.info('Ative o GPS para o cliente acompanhar você no mapa.');
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [online, entregador]);

  const earnings = useMemo(() => {
    return history.reduce((sum, p) => sum + Number(p.preco || 0), 0);
  }, [history]);

  // Ações
  const acceptPedido = async (pedido) => {
    if (active) { toast.error('Você já tem um pedido em andamento.'); return; }
    setAcceptingId(pedido.id);
    const { error } = await supabase
      .from('pedidos')
      .update({
        entregador_id: entregador.id,
        status: 'aceito',
        aceito_at: new Date().toISOString(),
      })
      .eq('id', pedido.id)
      .eq('status', 'buscando_entregador') // lock otimista
      .is('entregador_id', null);
    setAcceptingId(null);

    if (error) {
      console.error(error);
      toast.error('Erro ao aceitar. Talvez outro entregador tenha aceito antes.');
      loadPedidos();
      return;
    }
    toast.success('Pedido aceito!');
    loadPedidos();
  };

  const advanceStatus = async () => {
    if (!active) return;
    const meta = STATUS_NEXT[active.status];
    if (!meta) return;

    // Antes de marcar entregue, exige foto de comprovante
    if (meta.next === 'entregue') {
      setProofOpen(true);
      return;
    }

    const updates = { status: meta.next };
    if (meta.tsField) updates[meta.tsField] = new Date().toISOString();

    const { error } = await supabase
      .from('pedidos')
      .update(updates)
      .eq('id', active.id);
    if (error) {
      console.error(error);
      toast.error('Erro ao atualizar status.');
      return;
    }
    toast.success('Status atualizado.');
    loadPedidos();
  };

  // Comprovante de entrega: upload da foto + finalização via RPC
  const finalizarComComprovante = async () => {
    if (!active || !proofFile) return;
    setSubmittingProof(true);

    const ext = (proofFile.name.split('.').pop() || 'jpg').toLowerCase();
    const path = `${entregador.id}/${active.codigo_rastreio}-${Date.now()}.${ext}`;

    const { error: upErr } = await supabase.storage
      .from('delivery-proof')
      .upload(path, proofFile, { contentType: proofFile.type });
    if (upErr) {
      setSubmittingProof(false);
      toast.error('Erro no upload: ' + upErr.message);
      return;
    }

    // Signed URL com TTL longo (90 dias) — cliente verá no rastreio
    const { data: signed } = await supabase.storage
      .from('delivery-proof')
      .createSignedUrl(path, 60 * 60 * 24 * 90);

    const { data, error } = await supabase.rpc('entregador_finaliza_entrega', {
      p_pedido_id:        active.id,
      p_comprovante_url:  signed?.signedUrl ?? null,
      p_comprovante_path: path,
    });
    setSubmittingProof(false);

    if (error || !data) {
      console.error(error);
      toast.error('Erro ao finalizar entrega.');
      return;
    }
    toast.success('Entrega concluída!');
    setProofOpen(false);
    setProofFile(null);
    setProofPreview(null);
    loadPedidos();
    loadWallet();
  };

  // Helper: abrir endereço no Google Maps
  const mapsUrl = (addr) =>
    `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}&travelmode=driving`;

  const handleSignOut = () => supabase.auth.signOut();

  // Beep síncrono via Web Audio (sem asset). Toca um "ding ding" rápido.
  const playBeep = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const playTone = (freq, start, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
        gain.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + start + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + dur);
      };
      playTone(880, 0,    0.18); // dó
      playTone(1175, 0.2, 0.22); // ré
    } catch { /* ignore */ }
  };

  const requestNotifPermission = async () => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted' || Notification.permission === 'denied') {
      setNotifPermission(Notification.permission);
      return;
    }
    const result = await Notification.requestPermission();
    setNotifPermission(result);
    if (result === 'granted') {
      toast.success('Alertas ativados. Você ouvirá um som ao receber novo pedido.');
      playBeep();
    }
  };

  const showNotification = (pedido) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    try {
      const svc = SERVICES[pedido.servico_id] ?? SERVICES.frete_pequeno;
      const n = new Notification('Novo pedido disponível', {
        body: `${formatBRL(pedido.preco)} · ${Number(pedido.distancia_km).toFixed(1)} km · ${svc.name.replace('Fretz ', '')}`,
        icon: '/fretz-logo-light.png',
        tag: `new-order-${pedido.id}`,
      });
      n.onclick = () => { window.focus(); n.close(); };
    } catch { /* ignore */ }
  };

  // Detecta IDs novos em `available` e dispara beep + Notification.
  // Pula a primeira execução (todos são "novos" na chegada).
  useEffect(() => {
    const currentIds = new Set(available.map((p) => p.id));
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      knownAvailableIds.current = currentIds;
      return;
    }
    const novos = available.filter((p) => !knownAvailableIds.current.has(p.id));
    if (novos.length > 0 && online && !active) {
      playBeep();
      novos.forEach(showNotification);
    }
    knownAvailableIds.current = currentIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available]);

  // ─── Views ───────────────────────────────────────
  if (authState === 'loading') {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-brand-200 border-t-brand-600 animate-spin" />
      </div>
    );
  }

  if (authState === 'unauth') {
    return <Navigate to="/entregador/status" replace />;
  }

  if (authState === 'not_approved') {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
        <ToastContainer position="top-right" theme="colored" />
        <div className="w-full max-w-sm card p-8 text-center">
          <ClockIcon className="h-10 w-10 text-accent-500 mx-auto mb-3" />
          <p className="font-display font-bold text-ink-900 text-lg">Aguardando aprovação</p>
          <p className="text-sm text-ink-500 mt-2">
            Sua conta ainda não foi aprovada. Quando o time aprovar, você poderá começar a aceitar pedidos.
          </p>
          <Link to="/entregador/status" className="mt-5 btn-primary inline-flex">
            Ver status da inscrição
          </Link>
          <button onClick={handleSignOut} className="mt-3 btn-ghost text-sm gap-2">
            <ArrowLeftEndOnRectangleIcon className="h-4 w-4" /> Sair
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-50 pb-24">
      <ToastContainer position="top-right" theme="colored" />

      {/* Header app */}
      <header className="sticky top-0 z-40 bg-white border-b border-ink-100 shadow-soft">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between max-w-2xl">
          <div className="flex items-center gap-2">
            <Link to="/" aria-label="Fretz">
              <img src="/fretz-logo-light.png" alt="Fretz" className="h-7 w-auto" />
            </Link>
            <span className="text-ink-200">|</span>
            <span className="text-sm font-semibold text-ink-700">Entregador</span>
          </div>
          <div className="flex items-center gap-2">
            {notifPermission === 'default' && (
              <button
                onClick={requestNotifPermission}
                className="text-xs font-bold px-3 py-1.5 rounded-full transition flex items-center gap-1.5 bg-accent-100 text-accent-700 border border-accent-200"
                title="Receber alerta quando aparecer novo pedido"
              >
                <BellSlashIcon className="h-3.5 w-3.5" />
                Ativar alertas
              </button>
            )}
            {notifPermission === 'granted' && (
              <span className="text-xs px-2 py-1 rounded-full text-green-700 bg-green-50 border border-green-200 inline-flex items-center gap-1" title="Alertas ativados">
                <BellIcon className="h-3 w-3" />
              </span>
            )}
            <button
              onClick={() => setOnline((o) => !o)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full transition flex items-center gap-1.5
                ${online
                  ? 'bg-green-100 text-green-700 border border-green-200'
                  : 'bg-ink-100 text-ink-500 border border-ink-200'}`}
            >
              <PowerIcon className="h-3.5 w-3.5" />
              {online ? 'Online' : 'Offline'}
            </button>
            <button onClick={handleSignOut} className="p-2 rounded-lg hover:bg-ink-100 text-ink-500" title="Sair">
              <ArrowLeftEndOnRectangleIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 pt-6 max-w-2xl">

        {/* Resumo do dia */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="card p-4">
            <p className="text-xs text-ink-500">Entregas hoje</p>
            <p className="text-2xl font-display font-bold text-ink-900 mt-1">{history.length}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs text-ink-500">Ganhos do dia</p>
            <p className="text-2xl font-display font-bold text-brand-700 mt-1">{formatBRL(earnings)}</p>
          </div>
        </div>

        {/* Carteira */}
        <button
          onClick={() => setWalletOpen(true)}
          className="card p-4 mb-6 w-full flex items-center justify-between text-left hover:shadow-soft transition"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-accent-100 text-accent-700 flex items-center justify-center shrink-0">
              <BanknotesIcon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs text-ink-500">A receber</p>
              <p className="text-lg font-display font-bold text-accent-700">
                {formatBRL(wallet.aReceber)}
                <span className="text-xs text-ink-500 font-medium ml-2">
                  · {wallet.qtdPendente} pedido{wallet.qtdPendente !== 1 ? 's' : ''}
                </span>
              </p>
            </div>
          </div>
          <span className="text-xs text-brand-700 font-semibold">Ver carteira →</span>
        </button>

        {/* Pedido ativo */}
        {active && (
          <div className="card p-5 mb-6 ring-2 ring-brand-500 shadow-glow">
            <div className="flex items-center justify-between mb-3">
              <span className="badge-brand">Em andamento</span>
              <span className="text-xs text-ink-500">{active.codigo_rastreio}</span>
            </div>
            <p className="font-display font-bold text-lg text-ink-900">
              {SERVICES[active.servico_id]?.name || 'Pedido'} · {formatBRL(active.preco)}
            </p>
            <p className="text-xs text-ink-500 mt-0.5">
              {STATUS_NEXT[active.status]?.pastLabel || active.status}
            </p>

            <div className="mt-4 space-y-3 text-sm">
              <div className="flex gap-2">
                <div className="h-3 w-3 rounded-full bg-brand-600 mt-1 ring-4 ring-brand-100 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-ink-400 font-bold uppercase">Coleta</p>
                  <p className="text-ink-900">{active.endereco_coleta}</p>
                  <a
                    href={mapsUrl(active.endereco_coleta)}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-800"
                  >
                    <MapIcon className="h-3.5 w-3.5" /> Navegar até a coleta
                  </a>
                </div>
              </div>
              <div className="flex gap-2">
                <MapPinIcon className="h-4 w-4 text-accent-600 mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-ink-400 font-bold uppercase">Entrega</p>
                  <p className="text-ink-900">{active.endereco_entrega}</p>
                  {active.descricao_item && (
                    <p className="text-xs text-ink-500 mt-1">📦 {active.descricao_item}</p>
                  )}
                  <a
                    href={mapsUrl(active.endereco_entrega)}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-accent-700 hover:text-accent-800"
                  >
                    <MapIcon className="h-3.5 w-3.5" /> Navegar até a entrega
                  </a>
                </div>
              </div>
            </div>

            {/* Cliente */}
            <div className="mt-4 pt-4 border-t border-ink-100 flex items-center justify-between">
              <div>
                <p className="text-xs text-ink-400 font-bold uppercase">Cliente</p>
                <p className="text-sm text-ink-900 font-medium">{active.cliente_nome}</p>
              </div>
              <a
                href={`https://wa.me/55${(active.cliente_whatsapp || '').replace(/\D/g, '')}?text=${encodeURIComponent(`Olá! Sou o entregador do pedido ${active.codigo_rastreio}.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary text-xs gap-1.5"
              >
                <ChatBubbleLeftRightIcon className="h-4 w-4" />
                WhatsApp
              </a>
            </div>

            {/* Botão de avanço */}
            {STATUS_NEXT[active.status] && (
              <button
                onClick={advanceStatus}
                className="mt-4 btn-accent w-full py-3 gap-2"
              >
                <CheckCircleIcon className="h-5 w-5" />
                {STATUS_NEXT[active.status].label}
              </button>
            )}
          </div>
        )}

        {/* Estado offline */}
        {!active && !online && (
          <div className="card p-8 text-center">
            <PowerIcon className="h-10 w-10 text-ink-300 mx-auto mb-3" />
            <p className="font-display font-bold text-ink-900">Você está offline</p>
            <p className="text-sm text-ink-500 mt-2">
              Ative o status online para começar a receber pedidos.
            </p>
            <button
              onClick={() => setOnline(true)}
              className="mt-5 btn-primary"
            >
              <PowerIcon className="h-4 w-4" />
              Ficar online
            </button>
          </div>
        )}

        {/* Lista de disponíveis */}
        {!active && online && (
          <>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-ink-900">Pedidos disponíveis</h2>
              <button
                onClick={loadPedidos}
                className="btn-ghost text-xs gap-1.5"
                title="Atualizar"
              >
                <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </button>
            </div>

            {available.length === 0 ? (
              <div className="card p-8 text-center">
                <BoltIcon className="h-10 w-10 text-ink-300 mx-auto mb-3" />
                <p className="font-medium text-ink-700">Sem pedidos no momento</p>
                <p className="text-xs text-ink-500 mt-1">A lista atualiza a cada 15 segundos.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {available.map((p) => {
                  const svc = SERVICES[p.servico_id] ?? SERVICES.frete_pequeno;
                  return (
                    <div key={p.id} className="card p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="badge-brand mb-2">{svc.name.replace('Fretz ', '')}</p>
                          <p className="font-display font-bold text-2xl text-brand-700">
                            {formatBRL(p.preco)}
                          </p>
                          <p className="text-xs text-ink-500 mt-0.5">
                            {Number(p.distancia_km).toFixed(1)} km · ≈ {Math.round(p.tempo_estimado_min)} min
                          </p>
                        </div>
                        <div className="text-right">
                          <CurrencyDollarIcon className="h-5 w-5 text-accent-500 ml-auto" />
                          {p.retorno && <p className="text-[10px] text-ink-500 font-bold mt-1">+ retorno</p>}
                        </div>
                      </div>

                      <div className="mt-4 space-y-2 text-sm">
                        <div className="flex gap-2">
                          <div className="h-2.5 w-2.5 rounded-full bg-brand-600 mt-1.5 shrink-0" />
                          <p className="text-ink-700 line-clamp-1">{p.endereco_coleta}</p>
                        </div>
                        <div className="flex gap-2">
                          <MapPinIcon className="h-3.5 w-3.5 text-accent-600 mt-1 shrink-0" />
                          <p className="text-ink-700 line-clamp-1">{p.endereco_entrega}</p>
                        </div>
                      </div>

                      <button
                        onClick={() => acceptPedido(p)}
                        disabled={acceptingId === p.id}
                        className="mt-4 btn-primary w-full"
                      >
                        {acceptingId === p.id ? 'Aceitando…' : 'Aceitar pedido'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Histórico do dia */}
        {history.length > 0 && (
          <div className="mt-8">
            <h2 className="font-display font-bold text-ink-900 mb-3">Hoje</h2>
            <div className="card divide-y divide-ink-100">
              {history.map((p) => (
                <div key={p.codigo_rastreio} className="p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink-900 truncate">{p.endereco_entrega}</p>
                    <p className="text-xs text-ink-500">
                      {SERVICES[p.servico_id]?.name?.replace('Fretz ', '')} · {fmtTime(p.entregue_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    <span className="font-bold text-brand-700">{formatBRL(p.preco)}</span>
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Modal Comprovante de entrega */}
      {proofOpen && (
        <div className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center bg-ink-900/60 backdrop-blur-sm"
             onClick={() => !submittingProof && setProofOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between">
              <div>
                <p className="font-display font-bold text-ink-900">Comprovante de entrega</p>
                <p className="text-xs text-ink-500">Tire uma foto da encomenda no destino</p>
              </div>
              <button onClick={() => !submittingProof && setProofOpen(false)}
                      className="p-1.5 rounded-lg hover:bg-ink-100 text-ink-400" aria-label="Fechar">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 overflow-y-auto">
              {!proofPreview && (
                <label htmlFor="proof-input"
                       className="block card p-8 text-center cursor-pointer hover:bg-brand-50/40 transition border-2 border-dashed border-ink-200">
                  <CameraIcon className="h-10 w-10 text-ink-400 mx-auto" />
                  <p className="mt-3 font-display font-bold text-ink-900">Foto da entrega</p>
                  <p className="text-xs text-ink-500 mt-1">
                    Encomenda + ambiente, ou pessoa que recebeu.
                  </p>
                  <input
                    id="proof-input"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="sr-only"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 10 * 1024 * 1024) {
                        toast.error('Imagem muito grande (máx 10 MB).');
                        return;
                      }
                      setProofFile(f);
                      setProofPreview(URL.createObjectURL(f));
                    }}
                  />
                </label>
              )}

              {proofPreview && (
                <>
                  <img src={proofPreview} alt="Comprovante" className="w-full max-h-80 object-contain rounded-2xl bg-ink-50" />
                  <button onClick={() => { setProofFile(null); setProofPreview(null); }}
                          className="btn-ghost text-xs">
                    <XMarkIcon className="h-3 w-3" /> Trocar foto
                  </button>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-ink-100 flex gap-2">
              <button onClick={() => setProofOpen(false)}
                      disabled={submittingProof}
                      className="btn-ghost text-sm flex-1">
                Cancelar
              </button>
              <button onClick={finalizarComComprovante}
                      disabled={!proofFile || submittingProof}
                      className="btn-primary text-sm flex-1 gap-1.5">
                {submittingProof ? 'Enviando…' : (<><CheckCircleIcon className="h-4 w-4" /> Confirmar entrega</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Carteira */}
      {walletOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink-900/50 backdrop-blur-sm" onClick={() => setWalletOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between">
              <p className="font-display font-bold text-ink-900">Carteira</p>
              <button onClick={() => setWalletOpen(false)}
                      className="p-1.5 rounded-lg hover:bg-ink-100 text-ink-400" aria-label="Fechar">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div className="card p-4 bg-accent-50/40 border-accent-200">
                  <p className="text-xs text-ink-500">A receber</p>
                  <p className="text-xl font-display font-bold text-accent-700">{formatBRL(wallet.aReceber)}</p>
                </div>
                <div className="card p-4">
                  <p className="text-xs text-ink-500">Total ganho</p>
                  <p className="text-xl font-display font-bold text-ink-900">{formatBRL(wallet.totalGanho)}</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-2">Repasses recebidos</p>
                {repasses.length === 0 ? (
                  <div className="card p-6 text-center text-ink-400 text-sm">
                    Nenhum repasse ainda. O Fretz registra cada pagamento aqui.
                  </div>
                ) : (
                  <div className="card divide-y divide-ink-100">
                    {repasses.map((r) => (
                      <div key={r.id} className="p-4 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-ink-900">
                            {new Date(r.pago_em).toLocaleDateString('pt-BR')} · {r.metodo}
                          </p>
                          <p className="text-xs text-ink-500">
                            {r.quantidade_pedidos} pedido{r.quantidade_pedidos !== 1 ? 's' : ''}
                            {r.referencia ? ` · ${r.referencia}` : ''}
                          </p>
                        </div>
                        <span className="font-bold text-green-700 shrink-0">{formatBRL(r.valor_total)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <p className="text-xs text-ink-400 text-center pt-2">
                Em caso de dúvida sobre algum repasse, entre em contato com o time Fretz.
              </p>

              <div className="pt-4 border-t border-ink-100">
                <button
                  onClick={() => {
                    setWalletOpen(false);
                    setDeleteOpen(true);
                  }}
                  className="text-xs text-red-600 hover:text-red-700 underline"
                >
                  Excluir minha conta (LGPD)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal exclusão de conta (LGPD) */}
      {deleteOpen && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink-900/60 backdrop-blur-sm" onClick={() => !deletingAccount && setDeleteOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-ink-100">
              <p className="font-display font-bold text-ink-900">Excluir minha conta</p>
              <p className="text-xs text-ink-500 mt-1">Direito do titular · LGPD art. 18, VI</p>
            </div>
            <div className="px-6 py-5 space-y-4 overflow-y-auto">
              <div className="p-4 rounded-2xl bg-red-50 border border-red-200 text-sm text-ink-700 leading-relaxed">
                <p className="font-semibold text-red-700 mb-1">Esta ação é irreversível.</p>
                <p>
                  Seus dados pessoais (nome, e-mail, WhatsApp, CPF, CNH, fotos)
                  serão substituídos por valores fictícios e sua conta ficará
                  inacessível. Você não poderá mais aceitar pedidos.
                </p>
              </div>
              <div className="text-sm text-ink-600 leading-relaxed">
                <p>
                  Conforme exigido por lei (Código Tributário, CDC art. 27,
                  Marco Civil art. 15), <strong>os registros financeiros e
                  fiscais dos pedidos já entregues serão mantidos em forma
                  anonimizada</strong> pelos prazos legais. Esses dados
                  remanescentes não serão utilizados para qualquer finalidade
                  comercial.
                </p>
                <p className="mt-3">
                  Os repasses devidos a você até esta data serão pagos
                  normalmente.
                </p>
              </div>

              <div>
                <label className="label" htmlFor="del-motivo">Motivo (opcional)</label>
                <textarea id="del-motivo" className="input min-h-[60px] resize-y"
                          value={deleteReason}
                          onChange={(e) => setDeleteReason(e.target.value)}
                          placeholder="Ajuda a melhorar o serviço, mas não é obrigatório." />
              </div>

              <div>
                <label className="label" htmlFor="del-conf">
                  Para confirmar, digite <strong className="text-red-600">EXCLUIR</strong>
                </label>
                <input id="del-conf" className="input" type="text"
                       value={deleteConfirm}
                       onChange={(e) => setDeleteConfirm(e.target.value)}
                       placeholder="EXCLUIR" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-ink-100 flex items-center justify-between gap-3">
              <button
                onClick={() => setDeleteOpen(false)}
                disabled={deletingAccount}
                className="btn-ghost text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  if (deleteConfirm !== 'EXCLUIR') {
                    toast.error('Digite EXCLUIR para confirmar.');
                    return;
                  }
                  setDeletingAccount(true);
                  const { data, error } = await supabase.rpc('solicitar_exclusao_propria_conta', {
                    p_motivo: deleteReason || null,
                  });
                  setDeletingAccount(false);
                  if (error || !data) {
                    console.error(error);
                    toast.error('Erro ao processar. Tente novamente ou contate privacidade@fretz.com.br');
                    return;
                  }
                  toast.success('Conta anonimizada. Você será desconectado.');
                  setTimeout(() => supabase.auth.signOut(), 1500);
                }}
                disabled={deletingAccount || deleteConfirm !== 'EXCLUIR'}
                className="bg-red-600 hover:bg-red-700 disabled:bg-ink-200 disabled:text-ink-400 text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition"
              >
                {deletingAccount ? 'Processando…' : 'Confirmar exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntregadorApp;
